<template>
    <spinner
        :loading="loading">
        <b-table
            :striped="false"
            :hoverable="false"
            :data="attributeDefinitions"
            class="site-sticky-header table-height">
            <b-table-column
                v-slot="props"
                field="sequenceNumber"
                label="Seq#">
                {{ props.row.sequenceNumber }}
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="name"
                label="Name">
                {{ props.row.name }}
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="displayAs"
                label="Display Name">
                {{ props.row.displayAs }}
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="description"
                label="Description">
                <span :title="props.row.description">{{ props.row.description }}</span>
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="attributeType"
                label="Type">
                <router-link
                    v-if="isReferenceType(props.row.attributeType)"
                    :to="getLibraryLink(props.row.referenceLibraryName)">
                    {{ props.row.referenceLibraryName }}
                </router-link>
                <span v-else>{{ props.row.attributeType }}</span>
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="regex"
                label="Regex">
                <span :title="props.row.regex">{{ props.row.regex }}</span>
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="referenceDisplayMode"
                label="Item display">
                <span
                    v-if="isReferenceType(props.row.attributeType)">
                    {{ referenceDisplayMode(props.row.referenceDisplayMode) }}
                </span>
                <span v-else>&nbsp;</span>
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="inludeInidentity"
                label="In Identity">
                <bool-element :value="props.row.includeInIdentity" />
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="required"
                label="Required">
                <bool-element :value="props.row.required" />
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="defaultValue"
                label="Default value">
                {{ props.row.defaultValue ?? '' }}
            </b-table-column>
            <b-table-column
                v-if="isRelation"
                v-slot="props"
                field="treatAsAttribute"
                label="Display in related library">
                <bool-element
                    v-if="isCodeRefInIdentity(props.row)"
                    :value="props.row.treatAsAttribute" />
            </b-table-column>
            <b-table-column
                v-if="isRelation"
                v-slot="props"
                field="treatAsAttributeLabel"
                label="Label in related library">
                <span v-if="isCodeRefInIdentity(props.row) && props.row.treatAsAttribute">
                    {{ props.row.treatAsAttributeLabel }}
                </span>
            </b-table-column>
            <template #empty>
                <section class="section">
                    <div class="content has-text-grey has-text-centered">
                        <p>No attributes defined for this library</p>
                    </div>
                </section>
            </template>
        </b-table>
    </spinner>
</template>

<script>
    import BoolElement from '@/shared/components/BoolElement.vue';
    import { getLibraryLink } from '@/shared/helpers/routing';
    import Spinner from '@/shared/components/Spinner.vue';
    import { getAttributeDefinitions } from '@/shared/helpers/api';
    import { referenceDisplayMode } from '@/shared/helpers/utils';
    import { getNameLabel } from '@/shared/helpers/nameWithAliasHelper.ts';

    export default {
        components: {
            BoolElement,
            Spinner
        },
        props: {
            libraryName: {
                type: String,
                required: true
            },
            nameInIdentity: {
                type: Boolean,
                default: true
            },
            descriptionIsRequired: {
                type: Boolean,
                default: false
            },
            codeNameRegex: {
                type: String,
                default: ''
            },
            descriptionRegex: {
                type: String,
                default: ''
            },
            nameAlias: {
                type: String,
                default: ''
            },
            isRelation: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                loading: true,
                attributeDefinitions: null
            };
        },
        computed: {
            hasDataStyle() {
                return { disabled: !this.hasData };
            },
            hasData() {
                return this.attributeDefinitions !== null && this.attributeDefinitions.length > 0;
            }
        },
        async mounted() {
            const tempDefinitions = (await getAttributeDefinitions(this, this.libraryName)).sort((a, b) => a.sequenceNumber - b.sequenceNumber);
            tempDefinitions.unshift({
                name: 'IsValid',
                displayAs: '',
                description: 'Standard attribute: Is the item valid or not',
                attributeType: 'Bool',
                includeInIdentity: false,
                required: true,
                sequenceNumber: '-'
            });
            tempDefinitions.unshift({
                name: 'Description',
                displayAs: '',
                description: 'Standard attribute: The description of an item',
                attributeType: 'String',
                includeInIdentity: false,
                required: this.descriptionIsRequired,
                sequenceNumber: '-',
                regex: this.descriptionRegex
            });
            tempDefinitions.unshift({
                name: getNameLabel(this.nameAlias),
                displayAs: '',
                description: 'Standard attribute: The name of an item',
                attributeType: 'String',
                includeInIdentity: this.nameInIdentity,
                required: true,
                sequenceNumber: '-',
                regex: this.codeNameRegex
            });
            this.attributeDefinitions = tempDefinitions;
            this.loading = false;
        },
        methods: {
            isReferenceType(attributeType) {
                return attributeType.toUpperCase() === 'CODEREF';
            },
            isCodeRefInIdentity(row) {
                return row.attributeType?.toUpperCase() === 'CODEREF' && row.includeInIdentity;
            },
            getLibraryLink,
            referenceDisplayMode
        }
    };
</script>
